import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { dbUTCDatetimeToLocal, cloudLogLink } from '../../utils';
import { usePhalanxByReference } from '../../_services';
import {
  Divider,
  Grid,
  Header,
  Input,
  Segment,
  Table,
  Dropdown
} from 'semantic-ui-react';
import { useCurrentUser, servicesAPI } from '../../_services';
import { history } from '../../_helpers';

const countOptions = [
  { text: '10', value: 10 },
  { text: '25', value: 25 },
  { text: '50', value: 50 },
  { text: '100', value: 100 },
  { text: '200', value: 200 }
];

export const ListSessions = () => {
  const location = useLocation();

  const hubRef = location.pathname.split('/')[3];
  const csId = location.pathname.split('/')[4];
  const phalanxRef = hubRef + '_' + csId;

  const [hydra, setHydra] = useState(null);
  const [where, setWhere] = useState('');
  const [timestampFilter, setTimestampFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sessionData, setSessionData] = useState([]);
  const [customerNames, setCustomerNames] = useState([]);
  const [customerIds, setCustomerIds] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [numSessions, setNumSessions] = useState(10);

  const { currentUserCan } = useCurrentUser();

  const NO_UID = '00000000-0000-0000-0000-000000000000';

  const phalanx = usePhalanxByReference(phalanxRef);

  useEffect(() => {
    if (!phalanx) return;

    const parts = phalanx.reference.split('_cs');
    const hydraRef = parts[0];
    servicesAPI.InventoryServices('getHydraByReference', hydraRef, false).then(
      hydra => {
        setHydra(hydra);
      },
      error => {}
    );
  }, [phalanx]);

  useEffect(() => {
    if (!hydra || !phalanx) return;

    const parts = phalanx.reference.split('_');
    const csId = parts[parts.length - 1];
    setWhere(`${hydra.description} CS${csId}`);
  }, [hydra, phalanx]);

  useEffect(() => {
    if (!phalanx) return;

    servicesAPI
      .MeteringServices('getSessionsByPhalanx', phalanx.tag_id, 1, numSessions)
      .then(sessions => setSessions(sessions));
  }, [phalanx, numSessions]);

  useEffect(() => {
    if (sessions.length) {
      setSessionData(
        sessions.map((s, idx) => {
          const start =
            s.timestamp !== '' ? dbUTCDatetimeToLocal(s.timestamp) : '';
          const end =
            s.stop_timestamp !== ''
              ? dbUTCDatetimeToLocal(s.stop_timestamp)
              : '';

          return {
            ...s,
            idx: idx,
            start: start,
            end: end
          };
        })
      );
      getCustomerNames(sessions);
    }
  }, [sessions]);

  const getCustomerNames = sessions => {
    const customerToLanceIds = sessions
      .map(s => s.lance_customer_id)
      .filter(s => {
        return s !== NO_UID;
      });

    if (customerToLanceIds) {
      const values = {
        customer_to_lance_ids: customerToLanceIds
      };

      servicesAPI
        .CustomerServices('getCustomerNamesFromCustomerToLances', values)
        .then(
          response => {
            const names = sessions.map(s => {
              let customer = null;
              if (s.lance_customer_id !== NO_UID) {
                customer = response.find(
                  c => c.customer_to_lance_id === s.lance_customer_id
                );
              }

              return customer
                ? customer.forename + ' ' + customer.surname
                : '\\_(ツ)_/';
            });

            setCustomerNames(names);

            const ids = sessions.map(s => {
              let customer = null;
              if (s.lance_customer_id !== NO_UID) {
                customer = response.find(
                  c => c.customer_to_lance_id === s.lance_customer_id
                );
              }

              return customer ? customer.customer_id : '';
            });

            setCustomerIds(ids);
          },
          error => {}
        );
    }
  };

  const goToCloudLogLink = datetime => {
    servicesAPI
      .InventoryServices('getPhalanxByReference', phalanxRef)
      .then(phalanx => {
        const url = cloudLogLink(datetime, phalanx.reference);
        window.open(url, '_blank');
      });
  };

  return (
    <Segment>
      <Grid stretched>
        <Grid.Row>
          <Grid.Column>
            <Header as="h2">Charging Sessions - {where}</Header>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column mobile={8} tablet={6} computer={4}>
            <Input
              type="text"
              placeholder="filter timestamp"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setTimestampFilter('');
                }
              }}
              value={timestampFilter}
              onChange={(event, { value }) => {
                setTimestampFilter(value);
              }}
            />
          </Grid.Column>
          <Grid.Column mobile={8} tablet={6} computer={4}>
            <Input
              type="text"
              placeholder="filter status"
              icon={{
                name: 'close',
                link: true,
                onClick: () => {
                  setStatusFilter('');
                }
              }}
              value={statusFilter}
              onChange={(event, { value }) => {
                setStatusFilter(value);
              }}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Divider hidden />
      <Grid>
        <Grid.Column width={3}>
          <Grid.Row>
            <Header as="h4">No. sessions</Header>
            <Dropdown
              selection
              options={countOptions}
              onChange={(_, { value }) => {
                setNumSessions(value);
              }}
              value={numSessions}
            />
          </Grid.Row>
        </Grid.Column>
      </Grid>
      <Grid>
        <Table
          basic="very"
          selectable
          stackable
          headerRow={[
            'Session Started',
            'Session Ended',
            'Customer',
            'Status',
            'Charge Rate (kW)',
            'Total Consumption (Wh)'
          ]}
          renderBodyRow={({
            id,
            idx,
            start,
            end,
            timestamp,
            stop_timestamp,
            phalanx_tag_id,
            lance_customer_id,
            name,
            status,
            charge_rate,
            total_consumption
          }) => ({
            key: id,
            cells: [
              {
                key: 'start',
                content: (
                  <>
                    <span
                      onClick={() => {
                        history.push(`/admin/session/${id}`);
                      }}
                    >
                      {start}
                    </span>{' '}
                    {currentUserCan('ManageGoogleCloud') && (
                      <img
                        width={16}
                        height={16}
                        src="/images/gcp_logo.png"
                        onClick={event => {
                          goToCloudLogLink(timestamp);
                        }}
                        alt="Google Cloud logo"
                      />
                    )}
                  </>
                )
              },
              {
                key: 'end',
                content: (
                  <>
                    <span
                      onClick={() => {
                        history.push(`/admin/session/${id}`);
                      }}
                    >
                      {end}
                    </span>{' '}
                    {currentUserCan('ManageGoogleCloud') && end && (
                      <img
                        width={16}
                        height={16}
                        src="/images/gcp_logo.png"
                        onClick={event => {
                          goToCloudLogLink(stop_timestamp);
                        }}
                        alt="Google Cloud logo"
                      />
                    )}
                  </>
                )
              },
              {
                key: 'name',
                onClick: () => {
                  if (customerIds.length)
                    history.push(`/admin/customer/${customerIds[idx]}`);
                },
                content: customerNames.length ? customerNames[idx] : '?'
              },
              {
                key: 'status',
                onClick: () => {
                  history.push(`/admin/session/${id}`);
                },
                content: status
              },
              {
                key: 'charge_rate',
                onClick: () => {
                  history.push(`/admin/session/${id}`);
                },
                content: charge_rate.toFixed(0)
              },
              {
                key: 'total_consumption',
                onClick: () => {
                  history.push(`/admin/session/${id}`);
                },
                content: total_consumption
              }
            ]
          })}
          tableData={sessionData
            .filter(
              ({ timestamp }) =>
                !timestampFilter || timestamp.includes(timestampFilter)
            )
            .filter(
              ({ status }) => !statusFilter || status.includes(statusFilter)
            )}
        />
      </Grid>
    </Segment>
  );
};
